import React, { useState, useEffect } from 'react';
import '../Payment/summary.css';
import axios from 'axios';
import { useUser } from '../context/UserContext';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { CSVLink } from 'react-csv';

const CmTeamRevenue = () => {
    const [summaries, setSummaries] = useState([]);
    const [shopRevenues, setShopRevenues] = useState({});
    const [loading, setLoading] = useState(true);
    const [shopLoading, setShopLoading] = useState(false);
    const [error, setError] = useState('');
    const { userData } = useUser();
    const token = localStorage.getItem('auth-token');
    const apiUrl = process.env.REACT_APP_API_CM_TEAM;
    const today = new Date();
    const tenDaysAgo = new Date(today);
    tenDaysAgo.setDate(today.getDate() - 30);
    const [fromDate, setFromDate] = useState(tenDaysAgo.toISOString().split('T')[0]);
    const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);

    const academyToBranchMap = {
        "Main Warehouse": "38052",
        "71": "38055",
        "103": "39389",
        "99": "39390",
        "92": "39392",
        "83": "39393",
        "101": "39417",
        "70": "39964",
        "75": "39965",
        "95": "39966",
        "96": "39967",
        "97": "39968",
        "93": "39969",
        "94": "39970",
        "78": "39971",
        "80": "40075",
        "66": "40115",
        "76": "40129",
        "87": "40130",
        "72": "40131",
        "82": "40132",
        "69": "40133",
        "91": "40134",
        "64": "40135",
        "74": "40136",
        "77": "40691",
        "89": "40798",
        "90": "40808",
        "68": "40867",
        "79": "40868",
        "85": "40869",
    };

    const fetchShopRevenue = async (academyId, branchId) => {
        try {
            const requestBody = {
                fromDate: fromDate.split('-').reverse().join('-'),
                toDate: toDate.split('-').reverse().join('-')
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_SUMMER_VASY}?branchId=${branchId}`,
                requestBody,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'x-api-key': process.env.REACT_APP_API_KEY,
                    }
                }
            );

            if (response.data && response.data.externalResponse) {
                const totalShopRevenue = response.data.externalResponse.reduce((sum, item) => {
                    return sum + (item.totalPrice || 0);
                }, 0);

                return totalShopRevenue;
            }
            return 0;
        } catch (error) {
            console.error(`Error fetching shop revenue for academy ${academyId}, branch ${branchId}:`, error);
            return 0;
        }
    };

    const fetchAllShopRevenues = async (academyRevenueData) => {
        setShopLoading(true);
        const shopRevenueData = {};

        try {
            const promises = academyRevenueData.map(async (payment) => {
                const academyId = payment.academyId.toString();
                const branchId = academyToBranchMap[academyId];

                if (branchId) {
                    const revenue = await fetchShopRevenue(academyId, branchId);
                    shopRevenueData[academyId] = revenue;
                } else {
                    shopRevenueData[academyId] = 0;
                }
            });

            await Promise.all(promises);
            return shopRevenueData; // Return the data instead of setting state directly
        } catch (error) {
            console.error('Error fetching shop revenues:', error);
            return {};
        } finally {
            setShopLoading(false);
        }
    };

    const processRevenueData = (filteredRevenue, academyAmMapping, shopRevenuesData) => {
        return filteredRevenue.map(payment => {
            const amList = academyAmMapping[payment.academyId] || [];
            const amNames = amList.map(am => am.name).join(', ') || 'N/A';

            const shopRevenue = shopRevenuesData[payment.academyId.toString()] || 0;

            const playRevenue = (payment.membershipRevenue || 0) +
                (payment.playoRevenue || 0) +
                (payment.bulkBookingRevenue || 0) +
                (payment.walkinRevenue || 0);

            const totalRevenue = (payment.totalRevenue || 0) + shopRevenue;

            return {
                id: payment.academyId,
                am: amNames,
                academyName: payment.academyName,
                coachingRevenue: payment.coachingRevenue || 0,
                playRevenue: playRevenue,
                shopRevenue: shopRevenue,
                rentalsRevenue: payment.rentalsRevenue || 0,
                othersRevenue: payment.othersRevenue || 0,
                totalRevenue: totalRevenue,
                membershipCount: payment.membershipCount || 0,
                membershipRevenue: payment.membershipRevenue || 0,
                playoRevenue: payment.playoRevenue || 0,
                walkinRevenue: payment.walkinRevenue || 0,
                bulkBookingRevenue: payment.bulkBookingRevenue || 0,
            };
        });
    };

    const fetchData = async () => {
        setError('');
        setLoading(true);
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };

        let url = `${apiUrl}?startDate=${fromDate}&endDate=${toDate}`;

        if (userData?.userType === 'ACADEMY' && userData.academyIds?.length > 0) {
            const academyIdList = userData.academyIds.join(',');
            url += `&academyIds=${academyIdList}`;
        }

        try {
            const response = await axios.get(url, { headers });

            if (response.data.success) {
                const filteredRevenue = response.data.data.academyRevenue;
                const academyAmMapping = response.data.data.academyAmMapping || {};

                // Fetch all shop revenues and wait for the result
                const shopRevenuesData = await fetchAllShopRevenues(filteredRevenue);

                // Process the data with the shop revenues
                const revenueData = processRevenueData(filteredRevenue, academyAmMapping, shopRevenuesData);

                // Set both states
                setShopRevenues(shopRevenuesData);
                setSummaries(revenueData);
            }
        } catch (err) {
            setError("Failed to fetch summary data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fromDate, toDate, userData]);

    // This useEffect is now only for updates when shopRevenues changes externally
    useEffect(() => {
        if (summaries.length > 0 && Object.keys(shopRevenues).length > 0) {
            const updatedSummaries = summaries.map(summary => {
                const shopRevenue = shopRevenues[summary.id.toString()] || 0;
                const originalApiRevenue = summary.totalRevenue - summary.shopRevenue;
                const totalRevenue = originalApiRevenue + shopRevenue;

                return {
                    ...summary,
                    shopRevenue: shopRevenue,
                    totalRevenue: totalRevenue
                };
            });

            setSummaries(updatedSummaries);
        }
    }, [shopRevenues]);

    const headers = [
        { key: 'am', label: 'AM' },
        { key: 'academyName', label: 'Academy Name' },
        { key: 'coachingRevenue', label: 'Learn Revenue' },
        { key: 'playRevenue', label: 'Play Revenue' },
        { key: 'shopRevenue', label: 'Shop Revenue' },
        { key: 'rentalsRevenue', label: 'Rental Revenue' },
        { key: 'othersRevenue', label: 'Others Revenue' },
        { key: 'totalRevenue', label: 'Total Revenue' },
        { key: 'membershipCount', label: 'Membership Count' },
        { key: 'membershipRevenue', label: 'Membership Revenue' },
        { key: 'playoRevenue', label: 'Playo/Others Revenue' },
        { key: 'walkinRevenue', label: 'Walk-in Revenue' },
        { key: 'bulkBookingRevenue', label: 'Bulk Booking Revenue' },
    ];

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setrowsPerPage] = useState(50);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

    const handleItemsPerPageChange = (event) => {
        setrowsPerPage(Number(event.target.value));
        setCurrentPage(1);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setCurrentPage(1);
    };

    const handleFromDateChange = (event) => setFromDate(event.target.value);
    const handleToDateChange = (event) => setToDate(event.target.value);

    const filteredSummaries = summaries.filter((summary) => {
        const matchesSearchTerm =
            summary.academyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            summary.am?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            summary.coachingRevenue?.toString().includes(searchTerm) ||
            summary.playRevenue?.toString().includes(searchTerm) ||
            summary.shopRevenue?.toString().includes(searchTerm) ||
            summary.rentalsRevenue?.toString().includes(searchTerm) ||
            summary.othersRevenue?.toString().includes(searchTerm) ||
            summary.totalRevenue?.toString().includes(searchTerm) ||
            summary.membershipCount?.toString().includes(searchTerm) ||
            summary.membershipRevenue?.toString().includes(searchTerm) ||
            summary.playoRevenue?.toString().includes(searchTerm) ||
            summary.walkinRevenue?.toString().includes(searchTerm) ||
            summary.bulkBookingRevenue?.toString().includes(searchTerm);

        return matchesSearchTerm;
    });

    const handleSort = (columnKey) => {
        setSortConfig((prev) => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === "ascending" ? "descending" : "ascending",
        }));
    };

    const sortData = (data) => {
        const { key, direction } = sortConfig;
        return [...data].sort((a, b) => {
            if (!key) return 0;
            if (typeof a[key] === "string") return direction === "ascending" ? a[key].localeCompare(b[key]) : b[key].localeCompare(a[key]);
            return direction === "ascending" ? a[key] - b[key] : b[key] - a[key];
        });
    };

    const sortedData = sortData(filteredSummaries);

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(sortedData.length / rowsPerPage);

    const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);
    const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);

    const totals = {
        coachingRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.coachingRevenue, 0),
        playRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.playRevenue, 0),
        shopRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.shopRevenue, 0),
        rentalsRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.rentalsRevenue, 0),
        othersRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.othersRevenue, 0),
        totalRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.totalRevenue, 0),
        membershipCount: filteredSummaries.reduce((acc, curr) => acc + curr.membershipCount, 0),
        membershipRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.membershipRevenue, 0),
        playoRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.playoRevenue, 0),
        walkinRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.walkinRevenue, 0),
        bulkBookingRevenue: filteredSummaries.reduce((acc, curr) => acc + curr.bulkBookingRevenue, 0),
    };

    const generatePageNumbers = () => {
        const pages = [];
        const totalPagesToShow = 5;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                pages.push(i);
            }
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const renderPagination = () => {
        return generatePageNumbers().map((page, index) =>
            typeof page === 'number' ? (
                <button
                    key={index}
                    className={`page-number ${currentPage === page ? 'active' : ''}`}
                    onClick={() => setCurrentPage(page)}
                >
                    {page}
                </button>
            ) : (
                <span key={index} className="pagination-dots">
                    {page}
                </span>
            )
        );
    };

    const totalEntries = filteredSummaries.length;
    const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
    const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

    return (
        <div className="enrollment-container">
            <div className="sales-filters-login">
                <div className="floating-select">
                    <input
                        type="date"
                        className={`sales-filter-court-booking ${fromDate ? "has-value" : ""}`}
                        value={fromDate}
                        onChange={handleFromDateChange}
                        onFocus={(e) => e.target.showPicker()}
                        max={new Date().toISOString().split('T')[0]}
                    />
                    {fromDate && <label htmlFor="fromDate">Start Date</label>}
                </div>

                <div className="floating-select">
                    <input
                        type="date"
                        className={`sales-filter-academy ${toDate ? "has-value" : ""}`}
                        value={toDate}
                        onChange={handleToDateChange}
                        onFocus={(e) => e.target.showPicker()}
                        max={new Date().toISOString().split('T')[0]}
                    />
                    {toDate && <label htmlFor="toDate">To Date</label>}
                </div>
            </div>

            <div className="search-create-container">
                <div className="input-container">
                    <input
                        type="text"
                        id="searchInput"
                        placeholder=""
                        className={`search-input ${searchTerm ? "has-value" : ""}`}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                    <label htmlFor="searchInput" className="floating-label">
                        🔍 Search
                    </label>
                </div>
                <CSVLink
                    data={filteredSummaries}
                    headers={headers}
                    filename="CmTeamRevenue.csv"
                    className="btn btn-primary no-decoration"
                >
                    <button className="csv_button" type="button" >
                        <span
                            className="button__text"

                        >
                            Export CSV
                        </span>
                        <span className="button__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
                            </svg>
                        </span>
                    </button>
                </CSVLink>
            </div>

            <div className="items-and-dues">
                <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
                    <option value={10}>10 items per page</option>
                    <option value={50}>50 items per page</option>
                    <option value={75}>75 items per page</option>
                    <option value={100}>100 items per page</option>
                </select>
                <div className="pagination-selector">
                    <div className="button-group">
                        <div className="group-label">Items per page:</div>
                        <div className="button-options">
                            {[10, 50, 75, 100].map((option) => (
                                <button
                                    key={option}
                                    onClick={() =>
                                        handleItemsPerPageChange({ target: { value: option } })
                                    }
                                    className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                                >
                                    <span className="button-text">{option}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <button className="tt-action-btn" onClick={fetchData}>
                    <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
                    Refresh
                </button>
            </div>

            {(loading || shopLoading) && (
                <div className="premium-loader">
                    <div className="progress-track">
                        <div className="progress-bar"></div>
                    </div>
                    <div className="status-text">
                        <span>L</span>
                        <span>O</span>
                        <span>A</span>
                        <span>D</span>
                        <span>I</span>
                        <span>N</span>
                        <span>G</span>
                    </div>
                </div>
            )}
            {error && <div className="error-message">{error}</div>}
            {!loading && !shopLoading && !error && filteredSummaries.length === 0 && (
                <div className="error-message">No data available</div>
            )}

            <div className="tt-table-container">
                <table className="tt-table">
                    <thead>
                        <tr>
                            {headers.map(({ key, label }, index) => (
                                <th
                                    key={key}
                                    className={`challenges-column ${index === 7 ? 'vertical-divider' : ''}`}
                                    onClick={() => handleSort(key)}
                                >
                                    <div className="th-content">
                                        <span>{label}</span>
                                        <FiChevronDown
                                            className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                                                } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                                        />
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {currentRows.map(summary => (
                            <tr key={summary.id}>
                                <td>{summary.am}</td>
                                <td>{summary.academyName}</td>
                                <td>{summary.coachingRevenue.toFixed(2)}</td>
                                <td>{summary.playRevenue.toFixed(2)}</td>
                                <td>{summary.shopRevenue.toFixed(2)}</td>
                                <td>{summary.rentalsRevenue.toFixed(2)}</td>
                                <td>{summary.othersRevenue.toFixed(2)}</td>
                                <td className="vertical-divider-cell">{summary.totalRevenue.toFixed(2)}</td>
                                <td>{summary.membershipCount}</td>
                                <td>{summary.membershipRevenue.toFixed(2)}</td>
                                <td>{summary.playoRevenue.toFixed(2)}</td>
                                <td>{summary.walkinRevenue.toFixed(2)}</td>
                                <td>{summary.bulkBookingRevenue.toFixed(2)}</td>
                            </tr>
                        ))}
                        <tr className="totals-row">
                            <td><strong>Total</strong></td>
                            <td>-</td>
                            <td><strong>{totals.coachingRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.playRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.shopRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.rentalsRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.othersRevenue.toFixed(2)}</strong></td>
                            <td className="vertical-divider-cell"><strong>{totals.totalRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.membershipCount}</strong></td>
                            <td><strong>{totals.membershipRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.playoRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.walkinRevenue.toFixed(2)}</strong></td>
                            <td><strong>{totals.bulkBookingRevenue.toFixed(2)}</strong></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="pagination">
                <div className="showing-entries">
                    Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
                </div>
                <button className="prev-button" onClick={handlePrevPage} disabled={currentPage === 1}>Previous</button>
                {renderPagination()}
                <button className="next-button" onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
            </div>
        </div>
    );
};

export default CmTeamRevenue;